type InputEventType = 'focus' | 'blur' | 'change' | 'input' | 'select'

export const useGA4Tracking = () => {
	const { trackEvent } = useGA4EventQueue()
	const { getAudienceProperties } = useVisitorTracking()

	// Base tracking with audience properties only
	const track = (eventName: string, parameters?: Record<string, any>) => {
		const audienceProps = getAudienceProperties()

		// Only add timestamp if it's a custom implementation need (e.g. for a custom audience)
		// GA4 adds its own timestamp automatically
		const enrichedParams = {
			...parameters,
			...audienceProps
		}

		trackEvent(eventName, enrichedParams)
	}

	// Input tracking
	const trackInputEvent = (
		eventType: InputEventType,
		fieldId: string,
		formId: string,
		parameters?: Record<string, any>
	) => {
		track(`input_${eventType}`, {
			form_id: formId,
			field_id: fieldId,
			...parameters
		})
	}

	// Error tracking
	const trackError = (message: string, source: string, fatal = false) => {
		track('exception', {
			description: `${source}: ${message}`.substring(0, 150),
			fatal
		})
	}

	// Navigation tracking
	const trackNavigation = (item: string, location: string) => {
		track('navigation_click', {
			link_text: item,
			link_location: location
		})
	}

	// Engagement tracking
	const trackEngagement = (action: string, target: string, timeMs?: number) => {
		const params: Record<string, any> = {
			engagement_action: action,
			engagement_target: target
		}

		if (timeMs && timeMs > 0) {
			params.engagement_time_ms = Math.round(timeMs)
		}

		track('user_engagement', params)
	}

	// Form submission
	const trackFormSubmit = (formId: string, formType: string, parameters?: Record<string, any>) => {
		track('form_submit', {
			form_id: formId,
			form_type: formType,
			...parameters
		})
	}

	// Button click tracking
	const trackButtonClick = (buttonId: string, buttonType: 'button' | 'submit' | 'reset' | 'select', parameters?: Record<string, any>) => {
		track('button_click', {
			button_id: buttonId,
			button_type: buttonType,
			...parameters
		})
	}

	// Service plan tracking using standard e-commerce events
	const trackServicePlan = (
		action: 'view' | 'select' | 'purchase',
		tier: number,
		speed: number,
		price: number
	) => {
		const eventMap = {
			view: 'view_item',
			select: 'add_to_cart',
			purchase: 'purchase'
		}

		track(eventMap[action], {
			currency: 'USD',
			value: price,
			items: [ {
				item_id: `tier_${tier}`,
				item_name: `Service Tier ${speed} Mbps`,
				item_category: 'internet_service',
				price: price,
				quantity: 1
			} ]
		})
	}

	return {
		track,
		trackInputEvent,
		trackError,
		trackNavigation,
		trackEngagement,
		trackFormSubmit,
		trackServicePlan,
		trackButtonClick
	}
}
